import { PLACEHOLDER_DISPLAY_NAMES } from '@databases/templates/types';
import { type KnownUser } from '@databases/types/confluence';
import type {
  DatabaseField,
  DatabaseFieldType,
  DatabaseValue
} from '@databases/types/database';
import { isNonEmptyUserValue } from '@databases/utils/database/field-types/user';

import { DatabaseGroup } from './database-group';

export class DatabaseGroupUserField extends DatabaseGroup<DatabaseFieldType.USER> {
  readonly user: KnownUser | undefined;

  constructor(
    key: string,
    selectUserById: (props: { accountId: string }) => KnownUser | undefined,
    groupField: DatabaseField<DatabaseFieldType.USER>,
    value?: DatabaseValue<DatabaseFieldType.USER>,
    entryId?: string
  ) {
    const firstUser = extractFirstUserValue(value);
    super(key, groupField, firstUser, entryId);

    if (firstUser) {
      this.user = getKnownUserForFieldValue(firstUser, selectUserById);
    }
  }

  override get displayValue(): string | undefined {
    if (this.user?.displayName) {
      return this.user.displayName;
    }

    const accountId = this.fieldValue
      ? getUserAccountIdForFieldValue(this.fieldValue)
      : undefined;
    if (!accountId) {
      return undefined;
    }

    const DUMMY_PREFIX = 'dummy-';
    if (accountId.startsWith(DUMMY_PREFIX)) {
      return (
        PLACEHOLDER_DISPLAY_NAMES[accountId] ??
        PLACEHOLDER_DISPLAY_NAMES[`${DUMMY_PREFIX}0`]
      );
    }

    const userValue = Array.isArray(this.fieldValue?.value)
      ? this.fieldValue.value[0]
      : undefined;
    if (userValue && isNonEmptyUserValue(userValue) && userValue.displayName) {
      return userValue.displayName;
    }

    return undefined;
  }

  static generateKeyForFieldValue(
    fieldValue: DatabaseValue<DatabaseFieldType.USER>
  ): string {
    return typeof fieldValue.value === 'string'
      ? fieldValue.value
      : (getUserAccountIdForFieldValue(fieldValue) ?? '');
  }
}

/** Returns the user account ID for a given user field value. */
function getUserAccountIdForFieldValue(
  userFieldValue: DatabaseValue<DatabaseFieldType.USER>
): string | undefined {
  if (
    !Array.isArray(userFieldValue.value) ||
    userFieldValue.value.length === 0 ||
    !isNonEmptyUserValue(userFieldValue.value[0])
  ) {
    return undefined;
  }

  return userFieldValue.value[0].accountId;
}

/**
 * Reduces a multi-user field value to only the first user.
 */
function extractFirstUserValue(
  value: DatabaseValue<DatabaseFieldType.USER> | undefined
): DatabaseValue<DatabaseFieldType.USER> | undefined {
  if (!value || !Array.isArray(value.value) || value.value.length <= 1) {
    return value;
  }
  return {
    ...value,
    value: [value.value[0]]
  };
}

/** Returns a KnownUser for a given user field value. */
function getKnownUserForFieldValue(
  userFieldValue: DatabaseValue<DatabaseFieldType.USER>,
  selectUserById: (props: { accountId: string }) => KnownUser | undefined
): KnownUser | undefined {
  const userAccountId = getUserAccountIdForFieldValue(userFieldValue);
  if (!userAccountId) {
    return undefined;
  }

  return selectUserById({ accountId: userAccountId });
}
